import { Box, VStack } from "@chakra-ui/react";
import { FC } from "react";
import { BlockContainer } from "src/atoms";
import Renderer from "src/lib/renderer";
import { heading2, heading3 } from "src/lib/renderer/elements/headings";
import paragraph from "src/lib/renderer/elements/paragraph";
import { Element } from "@prismicio/react";
import { Helmet } from "react-helmet";
import { sanitizeStorylaneUrl, sanitizeArcadeUrl } from "src/templates/PageScript/utils";

const BlockEmbedStorylane: FC<
  (
    | Gatsby.PageBlockEmbedStorylaneFragment
    | Gatsby.PageLandingBlockEmbedStorylaneFragment
  ) & { index: number }
> = ({ primary }) => {
  if (!primary) return null;

  const { content, storylane_url } = primary;
  const rawUrl = storylane_url ?? undefined;
  const safe_storylane_url = sanitizeStorylaneUrl(rawUrl);
  const safe_arcade_url = sanitizeArcadeUrl(rawUrl);

  if (!safe_storylane_url && !safe_arcade_url) {
    return null;
  }

  const isArcade = !!safe_arcade_url;
  const embedUrl = safe_storylane_url || safe_arcade_url;

  // Prismic often returns an empty paragraph when no content is provided
  const hasContent = !!(
    content?.raw &&
    Array.isArray(content.raw) &&
    content.raw.some((block: any) => block.text && block.text.trim().length > 0)
  );

  return (
    <BlockContainer paddingY={{ base: "space-40", md: "space-64" }}>
      <VStack w="full" spacing="space-32" alignItems="center">
        {hasContent ? (
          <Box w="full" textAlign="center">
            <Renderer
              overrides={{
                [Element.heading2]: (args) =>
                  heading2({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.heading3]: (args) =>
                  heading3({ ...args, overrideProps: { textAlign: "center" } }),
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      textColor: "grey-1",
                      textAlign: "center",
                    },
                  }),
              }}
              field={content}
            />
          </Box>
        ) : null}

        <Box w="full">
          {!isArcade && (
            <Helmet>
              <script src="https://js.storylane.io/js/v1/storylane.js" />
            </Helmet>
          )}
          <Box
            className={isArcade ? "arcade-embed" : "sl-embed"}
            border="1px solid"
            borderColor="border-primary"
            sx={{
              position: "relative",
              paddingBottom: isArcade ? "calc(56.25% + 41px)" : "calc(53.74% + 26px)",
              width: "100%",
              height: "0",
              transform: "scale(1)",
              borderRadius: "intermediate",
              boxShadow: "card",
              bg: "white",
            }}
          >
            <iframe
              allow={isArcade ? "clipboard-write" : "fullscreen; camera; microphone"}
              className={isArcade ? "" : "sl-demo"}
              name={isArcade ? undefined : "sl-embed"}
              src={embedUrl}
              referrerPolicy="strict-origin-when-cross-origin"
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "inherit",
              }}
              allowFullScreen
            />
          </Box>
        </Box>
      </VStack>
    </BlockContainer>
  );
};

export default BlockEmbedStorylane;
